import {
  DocumentSnapshot,
  FieldValue,
  Firestore,
  Timestamp,
  runTransaction,
  serverTimestamp,
  updateDoc,
} from 'firebase/firestore';
import {ObservableStatus} from 'reactfire';
import {FirestoreWritable} from './FirestoreWritable';
import {Document, Status} from './types';
import {substituteServerTimestamp} from '#common/firebase/converters';
import {toDotNotation} from '#common/firebase/documents';
import {
  Entity,
  EntityUpdate,
  EntityWithMetadata,
  MetadataField,
  MetadataPropertyUpdate,
  ServerTimestampSentinelValue,
  UpdateMetadata,
} from '#common/models/Entity';

/**
 * Checks if a value is a Firebase type that should be preserved during sanitization.
 */
const isPreservedType = (value: unknown): boolean =>
  value instanceof Date || value instanceof Timestamp || value instanceof FieldValue;

export class FirestoreDocument<T extends Entity> extends FirestoreWritable<T> implements Document<T> {
  private readonly updateMetadata: UpdateMetadata;

  constructor(
    firestore: Firestore,
    currentUserId: string,
    private readonly document: ObservableStatus<DocumentSnapshot<EntityWithMetadata<T>>>,
    private readonly observable: ObservableStatus<EntityWithMetadata<T>>,
  ) {
    super(firestore, currentUserId);
    this.updateMetadata = Object.freeze({updatedBy: this.currentUserId, updatedTime: ServerTimestampSentinelValue});
    this.create = this.create.bind(this);
    this.delete = this.delete.bind(this);
    this.exists = this.exists.bind(this);
    this.get = this.get.bind(this);
    this.isLoading = this.isLoading.bind(this);
    this.observe = this.observe.bind(this);
    this.patch = this.patch.bind(this);
    this.status = this.status.bind(this);
    this.update = this.update.bind(this);
    this.updateTransactionally = this.updateTransactionally.bind(this);
  }

  create(entity: T): Promise<void> {
    return this.createDocument(this.document.data.ref, entity);
  }

  async delete(): Promise<void> {
    const meta: MetadataPropertyUpdate = {
      [MetadataField]: {
        deleted: true,
        deletedBy: this.currentUserId,
        deletedTime: ServerTimestampSentinelValue,
      },
    };
    const data = substituteServerTimestamp(
      meta as unknown as Record<string, unknown>,
      serverTimestamp,
      isPreservedType,
    );
    await updateDoc(this.document.data.ref, toDotNotation(data as unknown as EntityUpdate<T>));
  }

  exists(): boolean {
    if (!this.document.data.exists()) {
      return false;
    }
    const meta = this.document.data.data()[MetadataField];
    return meta.deleted === false;
  }

  get(): EntityWithMetadata<T> {
    if (this.observable.error) {
      throw this.observable.error;
    }
    return this.observable.data;
  }

  get id(): string {
    return this.document.data.id;
  }

  isLoading(): boolean {
    return this.status() === 'loading';
  }

  observe(): ObservableStatus<T> {
    return this.observable;
  }

  patch(update: EntityUpdate<T>): Promise<void> {
    const data = substituteServerTimestamp(
      {
        ...update,
        [MetadataField]: this.updateMetadata,
      },
      serverTimestamp,
      isPreservedType,
    );
    return updateDoc(this.document.data.ref, toDotNotation(data as unknown as EntityUpdate<T>));
  }

  status(): Status {
    return this.document.status;
  }

  update(update: EntityUpdate<T>): Promise<void> {
    return this.updateTransactionally((existing: T) => ({...existing, ...update}));
  }

  updateTransactionally(update: (existing: T) => T | Promise<T>): Promise<void> {
    return runTransaction(this.firestore, async (transaction) => {
      const snapshot = await transaction.get(this.document.data.ref);
      const existing = snapshot.data();
      if (!existing) {
        throw new Error('Document does not exist');
      }
      const updated = await Promise.resolve(update(existing as T));
      const data = substituteServerTimestamp(
        {
          ...updated,
          [MetadataField]: {
            ...existing[MetadataField],
            ...this.updateMetadata,
          },
        },
        serverTimestamp,
        isPreservedType,
      );
      transaction.set(this.document.data.ref, data);
    });
  }
}
